import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet-async';
import { Sparkles } from 'lucide-react';
import Breadcrumb from '../common/Breadcrumb';
import { pillars } from './methodData';

const MethodPage: React.FC = () => {
  const { ref: cardsRef, inView: cardsInView } = useInView({
    triggerOnce: true,
    threshold: 0.05,
  });

  const [activeTab, setActiveTab] = useState<Record<string, 'executive' | 'engineer'>>({});

  const getTab = (id: string) => activeTab[id] || 'executive';
  const toggleTab = (id: string, tab: 'executive' | 'engineer') => {
    setActiveTab((prev) => ({ ...prev, [id]: tab }));
  };

  const breadcrumbItems = [{ label: 'AI開発メソッド' }];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>AI開発メソッド | Senrigan</title>
        <meta
          name="description"
          content="Claude Code（AI）を活用した迅速・高品質なシステム開発メソッド。計画駆動開発、マルチエージェント分業、AIによるプロンプト精密化など、Senrigan独自の6本柱。"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 overflow-hidden bg-gradient-to-b from-[#0D1520] to-[#1B2A4A]">
        <div className="absolute inset-0 bg-[#4A6FA5]/10" />
        <div className="relative container mx-auto px-4">
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-4 md:px-5 lg:px-6 py-2 md:py-2.5 lg:py-3 rounded-full bg-black/40 backdrop-blur-sm mb-6 md:mb-10 border-2 border-[#B8860B]/40 shadow-lg shadow-[#B8860B]/10">
              <Sparkles className="w-4 md:w-4 lg:w-5 h-4 md:h-4 lg:h-5 text-[#B8860B]" />
              <span className="text-sm md:text-sm lg:text-base font-medium text-[#B8860B]">
                AI Development Method
              </span>
            </div>
          </div>

          <h1 className="mb-6 md:mb-10 leading-tight sm:leading-normal">
            <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-white">
              AIで実現する
              <span className="block mt-3 md:mt-4 bg-gradient-to-r from-[#D4A545] to-[#B8860B] text-transparent bg-clip-text">
                迅速・高品質な開発
              </span>
            </div>
          </h1>

          <p className="text-sm md:text-lg lg:text-lg text-white/90 leading-relaxed sm:max-w-lg sm:mx-auto px-4 md:px-0 text-justify">
            <span className="sm:hidden">
              Claude Codeを中核としたAI開発メソッドで、従来のシステム開発を根本から変革。計画駆動・品質自動化・マルチエージェント分業の6本柱で、迅速かつ安価に高品質なシステムを提供します。
            </span>
            <span className="hidden sm:inline text-left">
              Claude Codeを中核としたAI開発メソッドで、従来のシステム開発を根本から変革。
              計画駆動・品質自動化・マルチエージェント分業の6本柱で、迅速かつ安価に高品質なシステムを提供します。
            </span>
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Table of Contents */}
      <section className="py-8 md:py-12 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {pillars.map((pillar) => {
              const Icon = pillar.icon;
              return (
                <a
                  key={pillar.id}
                  href={`#${pillar.id}`}
                  className="group flex flex-col items-center gap-2 p-3 md:p-4 rounded-xl hover:bg-[#1B2A4A]/5 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#1B2A4A]/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Icon className="w-5 h-5 text-[#1B2A4A]" />
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-[#B8860B] font-bold">{pillar.number}</div>
                    <div className="text-xs md:text-sm font-medium text-gray-700 leading-tight">
                      {pillar.subtitle}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pillar Cards */}
      <section className="relative py-10 md:py-24 bg-[#F5F3EF]">
        <div className="container mx-auto px-4">
          <div ref={cardsRef} className="space-y-12 md:space-y-16">
            {pillars.map((pillar, index) => {
              const Icon = pillar.icon;
              const tab = getTab(pillar.id);
              return (
                <div
                  key={pillar.id}
                  id={pillar.id}
                  className={`scroll-mt-24 transform transition-all duration-1000 ${
                    cardsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="bg-white rounded-2xl shadow-sm border border-[#E8E6E1] overflow-hidden">
                    {/* Card Header */}
                    <div className="flex items-center gap-4 p-6 md:p-8 border-b border-[#E8E6E1]">
                      <div className="w-14 h-14 rounded-xl bg-[#1B2A4A]/10 flex items-center justify-center shrink-0">
                        <Icon className="w-7 h-7 text-[#1B2A4A]" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-[#B8860B] bg-[#B8860B]/10 px-2 py-0.5 rounded">
                            {pillar.number}
                          </span>
                          <span className="text-sm text-gray-500">{pillar.title}</span>
                          {pillar.isOriginal && (
                            <span className="text-xs font-bold text-[#B8860B] bg-[#B8860B]/10 px-2 py-0.5 rounded border border-[#B8860B]/30">
                              Senrigan Original
                            </span>
                          )}
                        </div>
                        <h2 className="text-xl md:text-2xl font-bold text-[#1B2A4A] mt-1">
                          {pillar.subtitle}
                        </h2>
                      </div>
                    </div>

                    {/* Perspective Tabs */}
                    <div className="px-6 md:px-8 pt-6">
                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={() => toggleTab(pillar.id, 'executive')}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            tab === 'executive'
                              ? 'bg-[#1B2A4A] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          経営者の方へ
                        </button>
                        <button
                          onClick={() => toggleTab(pillar.id, 'engineer')}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            tab === 'engineer'
                              ? 'bg-[#1B2A4A] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          エンジニアの方へ
                        </button>
                      </div>
                      <p className="text-gray-700 leading-relaxed pb-6 text-sm md:text-base">
                        {tab === 'executive' ? pillar.forExecutives : pillar.forEngineers}
                      </p>
                    </div>

                    {/* Details */}
                    <div className="px-6 md:px-8 pb-8">
                      <div className="grid md:grid-cols-3 gap-4">
                        {pillar.details.map((detail, i) => (
                          <div
                            key={i}
                            className="p-4 rounded-xl bg-[#F5F3EF] border border-[#E8E6E1]"
                          >
                            <h3 className="text-sm font-bold text-[#1B2A4A] mb-2">
                              {detail.title}
                            </h3>
                            <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
                              {detail.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#0D1520] to-[#1B2A4A]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            AI開発で、次のステージへ
          </h2>
          <p className="text-white/70 mb-8 max-w-lg mx-auto text-sm md:text-base">
            従来のシステム開発にかかっていた時間とコストを大幅に削減。
            まずはお気軽にご相談ください。
          </p>
          <a
            href="/#contact"
            className="group inline-flex items-center gap-2 px-8 py-4 bg-[#B8860B]/20 hover:bg-[#B8860B] text-[#B8860B] hover:text-white font-medium rounded-xl transition-all duration-300 border border-[#B8860B]/30 hover:border-[#B8860B]"
          >
            お問い合わせ
          </a>
        </div>
      </section>
    </div>
  );
};

export default MethodPage;
